import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { PLATFORMS, STORY_ASSET_TYPES, supportsAssetType } from '@/lib/clipPlatformFormats';
import { Plus } from 'lucide-react';

const VIDEO_STORY_TYPES = STORY_ASSET_TYPES.filter((t) => t.mediaKind === 'video');

/**
 * "Story" creation — Story is the shared asset-type concept from section 7,
 * not a new Production Tool. Clips owns the video/mixed-media kinds; a
 * Story here is a local sequence of EXISTING clips/videos/images (see
 * `stories` state in ClipsWorkspace.jsx) — never a new render pipeline.
 */
export function StoryCreatePanel({ onCreateStory }) {
  const [kind, setKind] = useState('video_story');
  const [platforms, setPlatforms] = useState(['instagram']);
  const togglePlatform = (key) => setPlatforms((ps) => (ps.includes(key) ? ps.filter((p) => p !== key) : [...ps, key]));
  const validPlatforms = platforms.filter((p) => supportsAssetType(p, kind));

  return (
    <div className="space-y-3" data-testid="story-create-panel">
      <p className="text-xs font-medium text-white">Story</p>
      <div className="grid grid-cols-2 gap-1.5">
        {VIDEO_STORY_TYPES.map((t) => (
          <Button key={t.key} size="sm" variant={kind === t.key ? 'default' : 'outline'} className={kind === t.key ? 'bg-[#3A7BFF] hover:bg-[#3A7BFF]/90 text-xs' : 'border-white/10 text-xs'} onClick={() => setKind(t.key)} data-testid={`story-kind-${t.key}`}>
            {t.label}
          </Button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {PLATFORMS.map((p) => {
          const supported = supportsAssetType(p.key, kind);
          return (
            <label key={p.key} className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs ${supported ? 'border-white/10 bg-white/[0.03] text-white cursor-pointer' : 'border-white/5 text-[#555E6E] cursor-not-allowed opacity-60'}`} data-testid={`story-platform-option-${p.key}`}>
              <Checkbox checked={platforms.includes(p.key)} disabled={!supported} onCheckedChange={() => togglePlatform(p.key)} data-testid={`story-platform-checkbox-${p.key}`} />
              {p.label}{!supported && <span className="text-[9px] ml-auto">N/A</span>}
            </label>
          );
        })}
      </div>
      <Button size="sm" className="w-full bg-[#3A7BFF] hover:bg-[#3A7BFF]/90" disabled={validPlatforms.length === 0} onClick={() => onCreateStory(validPlatforms)} data-testid="story-create-button">
        <Plus className="h-4 w-4 mr-2" />Create Story ({validPlatforms.length || 0} variant{validPlatforms.length === 1 ? '' : 's'})
      </Button>
      <p className="text-[10px] text-[#555E6E]">Starts empty — add pages from existing clips, videos or an image URL in the Clips category.</p>
    </div>
  );
}
